/**
 * ⟦C50 · A-169 §2⟧ **Choisir un pattern du Montage parmi ceux calculés hors ligne.**
 *
 * Matt, 01/10/2026 : *« […] fais-les calés sur la musique dès le départ, comme ça tu sélectionnes en
 * random parmi ceux calculés. »* Ce module est la sélection, et RIEN d'autre : il ne calcule aucun
 * carré. Les carrés sont dans `content/montage-patterns.json` (`scripts/montage-patterns.py`), chacun sur
 * une attaque audible du morceau.
 *
 * ⟦C54 · A-171⟧ **De 8 à 30 coups, dans une manche de durée fixe.** Le nombre de coups d'un pattern est un
 * PALIER (`8, 12, 16, 20, 24, 30`) : le niveau de difficulté en fixe un nombre visé (continu, il monte en cours
 * de palier d'exigence), et on tire, seedé, l'un des deux paliers qui l'encadrent, d'autant plus souvent le plus
 * haut que le nombre visé en est proche. Un morceau trop lent pour porter 30 coups dans la durée de la manche
 * (un coup toutes les 420 ms au plus serré) joue son maximum : il reste dans le tirage, il n'en sort pas.
 *
 * **Pur** : aucun accès disque, aucune horloge, aucun aléa propre. L'aléa est INJECTÉ (`indexPiste`, `u`,
 * `uDensite`) : le serveur le tire de son RNG seedé (`server/src/atelier/rng.ts`), le bac à sable du client de
 * `draw()`. Mêmes entrées ⇒ même pattern, donc `validate.ts` rejoue exactement ce que le joueur avait.
 */
const borne01 = (u) => Math.max(0, Math.min(0.999999999, u));
/** Les nombres de coups que les niveaux d'un format peuvent viser, croissants, sans doublon. */
export function paliersDuFormat(fichier, format) {
    return [...new Set(fichier.niveaux[format])].sort((a, b) => a - b);
}
/**
 * Le palier servi pour un nombre de coups visé `vises` (continu) : celui d'en dessous ou celui d'au-dessus,
 * tiré par `u`, d'autant plus souvent le plus haut que `vises` s'en rapproche. `vises` sur un palier le sert
 * toujours ; hors de la plage, le palier de l'extrémité. À `u` fixé, le palier ne descend jamais quand `vises`
 * monte : la difficulté ne recule pas.
 */
export function choisirPalier(paliers, vises, u) {
    const premier = paliers[0];
    const dernier = paliers[paliers.length - 1];
    const v = Math.max(premier, Math.min(dernier, vises));
    let bas = premier;
    let haut = dernier;
    for (const p of paliers) {
        if (p <= v)
            bas = p;
        if (p >= v) {
            haut = p;
            break;
        }
    }
    if (haut === bas)
        return bas;
    return borne01(u) < (v - bas) / (haut - bas) ? haut : bas;
}
/** Les patterns d'un morceau pour ce format, ce nombre de coups et cette variante. */
export function patternsDuPalier(piste, format, coups, variante) {
    return piste.patterns.filter((p) => p.format === format && p.variante === variante && p.cuts.length === coups);
}
/** Le plus haut palier du format pour lequel le morceau a un pattern classique (0 s'il n'en a aucun). */
export function palierMaxDuMorceau(fichier, piste, format) {
    let max = 0;
    for (const n of paliersDuFormat(fichier, format)) {
        if (patternsDuPalier(piste, format, n, "classique").length > 0)
            max = n;
    }
    return max;
}
const MEMO_JOUABLES = new WeakMap();
/**
 * Les morceaux qui entrent dans le tirage : pour chaque format et chaque palier jusqu'à
 * `parametres.palier_commun_max`, au moins un pattern classique. Au-delà de ce palier (24 et 30 coups : un
 * morceau lent ne peut pas poser 30 coups de 420 ms d'écart minimum dans 20 s), le morceau joue son maximum.
 * La liste est FIXE d'une partie à l'autre, quel que soit le niveau : c'est ce qui garantit ⟦C38 · A-161 §3⟧,
 * jamais deux fois de suite le même morceau (le tirage sans répétition porte sur cette liste, sans repli d'un
 * morceau à l'autre).
 */
export function pistesJouables(fichier) {
    const memo = MEMO_JOUABLES.get(fichier);
    if (memo)
        return memo;
    const plafond = fichier.parametres.palier_commun_max;
    const jouables = fichier.pistes.filter((piste) => ["short", "longue"].every((format) => paliersDuFormat(fichier, format)
        .filter((n) => n <= plafond)
        .every((n) => patternsDuPalier(piste, format, n, "classique").length > 0)));
    MEMO_JOUABLES.set(fichier, jouables);
    return jouables;
}
/**
 * Le pattern tiré. `indexPiste` désigne UN morceau de `pistesJouables` (modulo leur nombre) : c'est lui qui
 * sert, et on ne change jamais de morceau pour contourner un trou. Le palier est tiré (`choisirPalier`). Dans le
 * morceau, la variante demandée ; si le morceau n'en a pas à ce palier, le jeu de base (`classique`) ; s'il n'a
 * rien à ce palier, le palier du dessous. Puis le `u`-ième des patterns restants. Un morceau jouable a toujours
 * un classique à chaque palier jusqu'à `palier_commun_max` : `null` seulement si le fichier n'a aucun morceau jouable.
 */
export function choisirPatternMontage(fichier, entree) {
    const jouables = pistesJouables(fichier);
    if (jouables.length === 0)
        return null;
    const piste = jouables[((Math.trunc(entree.indexPiste) % jouables.length) + jouables.length) % jouables.length];
    const paliers = paliersDuFormat(fichier, entree.format);
    const tire = choisirPalier(paliers, entree.vises, entree.uDensite);
    const variantes = entree.variante === "classique" ? ["classique"] : [entree.variante, "classique"];
    for (const n of paliers.filter((p) => p <= tire).reverse()) {
        for (const variante of variantes) {
            const eligibles = patternsDuPalier(piste, entree.format, n, variante);
            if (eligibles.length === 0)
                continue;
            const u = borne01(entree.u);
            return {
                piste,
                pattern: eligibles[Math.min(eligibles.length - 1, Math.floor(u * eligibles.length))],
                variante,
                coups: n,
            };
        }
    }
    return null;
}
/** Les carrés d'un pattern, en temps de jeu (zéro = `origineMs`), triés, avec leur nature pour le rendu. */
export function carresDuPattern(piste, pattern) {
    const doubles = new Set(pattern.doubles ?? []);
    const contretemps = new Set(pattern.contretemps ?? []);
    return pattern.cuts.map((indice, i) => {
        const carre = { atMs: piste.attaques[indice] - pattern.origineMs };
        if (doubles.has(i))
            carre.type = "double";
        else if (contretemps.has(i))
            carre.type = "contretemps";
        return carre;
    });
}
//# sourceMappingURL=montage-patterns.js.map